import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  cartItems: [],
  totals: { total: 0 },
  totalItems: 0,
};

const getEffectiveQty = (item) => {
  const quantity = parseFloat(item.quantity || 0);
  const unitStr = (item.units || item.unit || '').toLowerCase();
  const baseWeightMatch = item.name?.match(/(\d+)\s*(g|gm|gram|grams|gms)\b/i);
  const baseWeight = baseWeightMatch ? parseFloat(baseWeightMatch[1]) : null;
  const isWeightUnit = unitStr.includes('kg') || unitStr.includes('gram') || /\b(g|gms|gm)\b/.test(unitStr);
  if (baseWeight && baseWeight > 0 && isWeightUnit) {
    return quantity / (baseWeight / 1000);
  }
  return quantity;
};

const getItemPrice = (item) => {
  const isInclusive = item.price_includes_tax !== false;
  if (isInclusive) {
    return parseFloat(item.inclusive_price ?? item.base_price ?? 0);
  }
  return parseFloat(item.base_price ?? 0);
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartItems: (state, action) => {
      state.cartItems = action.payload;
      state.totalItems = action.payload.reduce((sum, item) => sum + (item.quantity || 0), 0);
      state.totals = action.payload.reduce(
        (acc, item) => {
          const price = getItemPrice(item);
          acc.total += price * getEffectiveQty(item);
          return acc;
        },
        { total: 0 }
      );
      localStorage.setItem('selectedProducts', JSON.stringify(state.cartItems));
    },
    addToCart: (state, action) => {
      const product = action.payload;
      const exist = state.cartItems.find((i) => i.id === product.id);

      const unitStr = (product.units || '').toLowerCase();
      const baseWeightMatch = product.name?.match(/(\d+)\s*(g|gm|gram|grams|gms)\b/i);
      const baseWeight = baseWeightMatch ? parseFloat(baseWeightMatch[1]) : null;
      const isWeightUnit = unitStr.includes('kg') || unitStr.includes('gram') || /\b(g|gms|gm)\b/.test(unitStr);
      let change = 1;
      if (baseWeight && baseWeight > 0 && isWeightUnit) {
        change = baseWeight / 1000;
      }

      let updated;
      if (exist) {
        updated = state.cartItems.map((i) =>
          i.id === product.id ? { ...i, quantity: Math.min(999999, parseFloat((i.quantity + change).toFixed(3))) } : i
        );
      } else {
        updated = [...state.cartItems, { ...product, quantity: change }];
      }
      state.cartItems = updated;
      state.totalItems = updated.reduce((sum, item) => sum + (item.quantity || 0), 0);
      state.totals = updated.reduce(
        (acc, item) => {
          const price = getItemPrice(item);
          acc.total += price * getEffectiveQty(item);
          return acc;
        },
        { total: 0 }
      );
      localStorage.setItem('selectedProducts', JSON.stringify(updated));
    },
    updateQuantity: (state, action) => {
      const { id, change } = action.payload;
      const updated = state.cartItems
        .map((i) =>
          i.id === id ? { ...i, quantity: Math.min(999999, Math.max(0, parseFloat((parseFloat(i.quantity || 0) + parseFloat(change || 0)).toFixed(3)))) } : i
        )
        .filter((i) => i.quantity > 0);
      state.cartItems = updated;
      state.totalItems = updated.reduce((sum, item) => sum + (parseFloat(item.quantity || 0)), 0);
      state.totals = updated.reduce(
        (acc, item) => {
          const price = getItemPrice(item);
          acc.total += price * getEffectiveQty(item);
          return acc;
        },
        { total: 0 }
      );
      localStorage.setItem('selectedProducts', JSON.stringify(updated));
    },
    removeItem: (state, action) => {
      const updated = state.cartItems.filter((item) => item.id !== action.payload);
      state.cartItems = updated;
      state.totalItems = updated.reduce((sum, item) => sum + (item.quantity || 0), 0);
      state.totals = updated.reduce(
        (acc, item) => {
          const price = getItemPrice(item);
          acc.total += price * getEffectiveQty(item);
          return acc;
        },
        { total: 0 }
      );
      localStorage.setItem('selectedProducts', JSON.stringify(updated));
    },
    setManualQuantity: (state, action) => {
      const { id } = action.payload;
      const quantity = Math.min(999999, parseFloat(action.payload.quantity));
      if (isNaN(quantity) || quantity < 0) return;
      const updated = state.cartItems.map((item) =>
        item.id === id ? { ...item, quantity } : item
      );
      state.cartItems = updated;
      state.totalItems = updated.reduce((sum, item) => sum + (item.quantity || 0), 0);
      state.totals = updated.reduce(
        (acc, item) => {
          const price = getItemPrice(item);
          acc.total += price * getEffectiveQty(item);
          return acc;
        },
        { total: 0 }
      );
      localStorage.setItem('selectedProducts', JSON.stringify(updated));
    },
    resetCart: (state) => {
      state.cartItems = initialState.cartItems;
      state.totals = initialState.totals;
      state.totalItems = initialState.totalItems;
      localStorage.removeItem('selectedProducts');
    },
  },
});

export const {
  setCartItems,
  addToCart,
  updateQuantity,
  removeItem,
  setManualQuantity,
  resetCart,
} = cartSlice.actions;

export default cartSlice.reducer;